import React, { useState, useRef, useEffect, useMemo, FC } from 'react';

import PropTypes from 'prop-types';

import { EntitlementsProject } from '@adsk/alloy-provider';
import { Box } from '@adsk/alloy-react-box';
import { safeSearchRegex } from '@adsk/alloy-react-helpers';
import { useLocale } from '@adsk/alloy-react-locale';
import SearchField from '@adsk/alloy-react-search-field';

import ProjectListProjectItem from './ProjectListProjectItem';
import ProjectRowLoader from './ProjectRowLoader';
import ProjectsError from './ProjectsError';
import ResultsLoader from './ResultsLoader';
import VirtualizedList from './VirtualizedList';
import { projectsListShape } from '../helpers/propTypes';

type ProjectPickerProps = {
  currentProjectId?: string;
  projects?: EntitlementsProject[];
  projectsLoading?: boolean;
  projectsError?: boolean;
  projectsRetry?: () => void;
  onClose?: () => void;
  onProjectClick?: (project?: object, url?: string) => void;
};

const ProjectPickerExternal: FC<ProjectPickerProps> = ({
  currentProjectId,
  projects,
  projectsLoading,
  projectsError,
  projectsRetry,
  onClose,
  onProjectClick,
}) => {
  const { T } = useLocale(({ locale }) => require(`../i18n/${locale}.json`));
  const searchRef = useRef<HTMLInputElement | null>(null);
  const [search, setSearch] = useState<string>('');

  const onSearchChange = (value: string) => {
    setSearch(value);
  };

  const projectsSearch = useMemo(
    () =>
      (projects &&
        projects.filter(
          (p) => p.name && safeSearchRegex(search).test(p.name),
        )) ||
      [],
    [projects, search],
  );

  useEffect(() => searchRef.current?.focus(), []);

  return (
    <Box width="360px">
      <Box padding="16px">
        <SearchField
          ref={searchRef}
          placeholder={T.translate('SEARCH_PROJECTS')?.toString()}
          value={search || ''}
          onChange={onSearchChange}
        />
      </Box>

      <Box typography="heading5" color="charcoal700" padding="8px 16px">
        {projectsLoading && <ResultsLoader />}
        {!projectsLoading &&
          `${projectsSearch.length} ${T.translate('PROJECTS')?.toString()}`}
      </Box>

      {search && !projectsSearch.length && (
        <Box
          typography="bodySmall"
          color="charcoal700"
          textAlign="center"
          padding="36px 0"
        >
          {T.translate('NO_RESULTS_FOUND')}
        </Box>
      )}

      {projectsLoading && <ProjectRowLoader />}

      {projectsError && <ProjectsError retry={projectsRetry} />}

      {!projectsError && !projectsLoading && !!projectsSearch.length && (
        <VirtualizedList
          size={projectsSearch.length}
          renderItem={(index) => {
            const item = projectsSearch[index];
            return (
              <ProjectListProjectItem
                key={item.id}
                item={item}
                currentProjectId={currentProjectId}
                onProjectClick={onProjectClick}
                onClose={onClose}
              />
            );
          }}
        />
      )}
    </Box>
  );
};

ProjectPickerExternal.displayName = 'ProjectPickerExternal';

ProjectPickerExternal.propTypes = {
  /** Available projects to select from */
  projects: projectsListShape,
  /** current selected project id */
  currentProjectId: PropTypes.string,
  /** show projects loader */
  projectsLoading: PropTypes.bool,
  /** is project entitlements ended in error */
  projectsError: PropTypes.bool,
  /** retry loading project entitlements */
  projectsRetry: PropTypes.func,
  /** menu close handler */
  onClose: PropTypes.func,
  /** project click handler */
  onProjectClick: PropTypes.func,
};

export default ProjectPickerExternal;
